import { mulberry32, seedFrom } from "./random.js";
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { items: 3, gap: 0.3 };
        case 2:
            return { items: 4, gap: 0.22 };
        case 3:
            return { items: 5, gap: 0.16 };
        case 4:
            return { items: 6, gap: 0.12 };
        case 5:
            return { items: 6, gap: 0.1 };
        case 6:
            return { items: 7, gap: 0.09 };
        case 7:
            return { items: 7, gap: 0.075 };
        /**
         * Seven items and a gap of six hundredths of the range.
         *
         * The floor on both axes. Eight items across a phone row leaves each one too small to compare
         * by eye, which would make it a game about screen resolution; and below about six hundredths
         * two neighbouring sizes are a few pixels apart, which is a game about the display.
         */
        default:
            return { items: 7, gap: 0.06 };
    }
}
export const ORDERING_MIN_DIFFICULTY = 1;
export const ORDERING_MAX_DIFFICULTY = 8;
export function generateOrdering(task, formId, trialIndex, difficulty) {
    if (difficulty < ORDERING_MIN_DIFFICULTY || difficulty > ORDERING_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { items, gap } = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    /**
     * Sizes are laid out on an even ladder and then jittered inside the space that remains.
     *
     * Drawing them at random would sometimes produce two items a hair apart, which is not a harder
     * trial but an unanswerable one — a child who cannot see a difference is not failing to order,
     * they are being asked to distinguish something that is not distinguishable. The ladder keeps
     * every neighbouring gap at or above the recipe's minimum, and the jitter stops the set being the
     * same evenly-spaced ramp every time.
     */
    const span = gap * (items - 1);
    const slack = Math.max(0, 1 - span);
    const jitter = slack / items;
    const ladder = Array.from({ length: items }, (_, i) => {
        const base = i * gap + (slack * i) / items;
        return Math.min(1, base + rand() * jitter);
    });
    // Shuffle into display positions, seeded so the board re-derives identically.
    const order = ladder.map((_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
    }
    const sizes = order.map((rank) => ladder[rank]);
    const answer = sizes
        .map((size, position) => ({ size, position }))
        .sort((a, b) => a.size - b.size)
        .map((entry) => entry.position);
    return { sizes, answer };
}
/** Whether a sequence of taps is the correct ascending order so far. */
export function orderingPrefixValid(round, tapped) {
    return tapped.every((position, i) => position === round.answer[i]);
}
/** Tapped every item, smallest first, with no wrong taps along the way. */
export function scoreOrderingTrial(round, tapped) {
    return tapped.length === round.answer.length && orderingPrefixValid(round, tapped);
}
//# sourceMappingURL=ordering.js.map